
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { Header } from '@/components/Header';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileCompleteness } from '@/hooks/useProfileCompleteness';
import { useNavigation } from '@/hooks/useNavigation';

const TermsOfService = () => {
  const [searchParams] = useSearchParams();
  const versionId = searchParams.get('version');
  const location = useLocation();
  const { toast } = useToast();
  const [accepted, setAccepted] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const { isComplete, isLoading: profileLoading, updateCompletionStatus } = useProfileCompleteness();
  const [isCheckingTerms, setIsCheckingTerms] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { navigateTo, navigateToProfile, navigateAfterFlow } = useNavigation();
  
  const { data: terms, isLoading } = useQuery({
    queryKey: ['terms', versionId],
    queryFn: async () => {
      console.log('Fetching terms of service version:', versionId);
      const { data, error } = await supabase
        .from('terms_of_service')
        .select('*')
        .eq(versionId ? 'id' : 'is_current', versionId ? parseInt(versionId) : true)
        .single();
        
      if (error) {
        console.error('Error fetching terms:', error);
        throw error;
      }
      console.log('Terms fetched:', data);
      return data;
    }
  });

  useEffect(() => {
    const checkAuthAndTerms = async () => {
      if (!isAuthenticated) {
        console.log('User is not authenticated, redirecting to auth page');
        navigateTo('/auth', { forceReplace: true });
        return;
      }
      
      if (user && terms) {
        try {
          const { data, error } = await supabase
            .from('user_terms_acceptance')
            .select('*')
            .eq('user_id', user.id)
            .eq('terms_id', terms.id)
            .maybeSingle();
            
          if (error) {
            console.error('Error checking terms acceptance:', error);
            setIsCheckingTerms(false);
            return;
          }
          
          console.log('Terms acceptance check result:', data);
          
          if (data && !location.state?.forceTermsReview) {
            console.log('User has already accepted these terms, redirecting to home');
            navigateTo('/', { delay: 100, forceReplace: true });
          } else {
            setIsCheckingTerms(false);
          }
        } catch (error) {
          console.error('Error in checkAuthAndTerms:', error);
          setIsCheckingTerms(false);
        }
      } else {
        setIsCheckingTerms(false);
      }
    };
    
    checkAuthAndTerms();
  }, [isAuthenticated, user, terms, location.state, navigateTo]);

  const acceptTerms = async () => {
    if (!terms || !isAuthenticated || !user) {
      console.log('Missing required data for accepting terms:', {
        terms: !!terms,
        isAuthenticated,
        user: !!user
      });
      return;
    }
    
    if (!accepted) {
      console.log('Terms not accepted by user (checkbox not checked)');
      toast({
        variant: "warning",
        title: "Terms Not Accepted",
        description: "Please check the box to accept the Terms of Service."
      });
      return;
    }
    
    try {
      setIsSubmitting(true);
      console.log('Starting terms acceptance process');
      
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ tos_accepted: true })
        .eq('id', user.id);
        
      if (profileError) {
        console.error('Error updating profile with ToS acceptance:', profileError);
        throw profileError;
      }
      
      const { error } = await supabase
        .from('user_terms_acceptance')
        .upsert({
          terms_id: terms.id,
          user_id: user.id,
          accepted_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,terms_id',
          ignoreDuplicates: false
        });

      if (error) {
        console.error('Error recording terms acceptance:', error);
        throw error;
      }

      console.log('Terms acceptance completed successfully');
      toast({
        variant: "success",
        title: "Terms Accepted",
        description: `Version ${terms.version} Terms of Service successfully accepted.`
      });
      
      setIsRedirecting(true);
      
      if (!isComplete) {
        console.log('Profile incomplete, redirecting to profile setup.');
        setTimeout(() => {
          navigateTo('/my-profile?edit=true&reason=tos', { 
            delay: 100, 
            forceReplace: true
          });
        }, 300);
      } else {
        console.log('Profile complete, redirecting to home with timestamp:', new Date().toISOString());
        setTimeout(() => {
          navigateAfterFlow({ delay: 100 });
        }, 300);
      }
    } catch (error: any) {
      console.error('Error in acceptTerms:', error);
      setIsRedirecting(false);
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Could not accept terms of service. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isViewOnly = !!versionId;

  if (isLoading || isCheckingTerms) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900"></div>
      </div>
    );
  }
  
  if (isRedirecting) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-gray-900 mb-4"></div>
        <h2 className="text-xl font-semibold mb-2">Redirecting...</h2>
        <p className="text-gray-500">Please wait while we prepare your experience</p>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="container mx-auto px-4 py-8 max-w-4xl mt-24">
        <Card>
          <CardHeader>
            <h1 className="text-2xl font-bold">Terms of Service</h1>
            <p className="text-sm text-muted-foreground">
              Version {terms?.version} - Published {new Date(terms?.published_at || '').toLocaleDateString()}
              {!terms?.is_current && " (Archive)"}
            </p>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <div className="bg-muted p-6 rounded-lg terms-content"
                dangerouslySetInnerHTML={{ __html: terms?.content || '' }}
              />
            </div>
            {!isViewOnly && (
              <div className="mt-6 flex items-center space-x-2">
                <Checkbox
                  id="accept"
                  checked={accepted}
                  onCheckedChange={(checked) => {
                    console.log('Checkbox state changed:', checked);
                    setAccepted(checked as boolean);
                  }}
                />
                <label
                  htmlFor="accept"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  I have read and agree to the Terms of Service
                </label>
              </div>
            )}
          </CardContent>
          <CardFooter className="flex justify-between">
            {isViewOnly ? (
              <Button variant="outline" onClick={() => window.close()}>
                Close
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => navigateAfterFlow()}>
                  Back
                </Button>
                <Button 
                  onClick={acceptTerms} 
                  disabled={!accepted || isSubmitting}
                  className={isSubmitting ? "opacity-70" : ""}
                >
                  {isSubmitting ? "Processing..." : "Accept Terms"}
                </Button>
              </>
            )}
          </CardFooter>
        </Card>
      </div>
    </>
  );
};

export default TermsOfService;
